import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plane, UserCheck, Building2, Briefcase, Ship, Heart, Route, ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * CoreServices — 7 primary services per client brief.
 *
 * Design rules:
 *   - Grid of 7 (3 + 4 layout, breaks nicely on all breakpoints)
 *   - Minimal text: title + ONE line description
 *   - Clean white background, subtle border, gold hover accent
 *   - First card ("Airport Transfers") links to dedicated landing page
 */

const SERVICES = [
  { key: 'airport',      icon: Plane,     link: (lang) => `/${lang}/porto-airport-private-transfer` },
  { key: 'executive',    icon: UserCheck, link: null },
  { key: 'hotel',        icon: Building2, link: null },
  { key: 'corporate',    icon: Briefcase, link: null },
  { key: 'cruise',       icon: Ship,      link: null },
  { key: 'wedding',      icon: Heart,     link: null },
  { key: 'long_distance',icon: Route,     link: null },
];

const ServiceCard = ({ service, t, lang }) => {
  const Icon = service.icon;
  const title = t(`services_v2.items.${service.key}.title`);
  const desc = t(`services_v2.items.${service.key}.desc`);

  const inner = (
    <div className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200/70 bg-white p-7 md:p-8 transition-all duration-500 hover:border-amber-300 hover:shadow-[0_20px_50px_-15px_rgba(245,158,11,0.25)] hover:-translate-y-1">
      {/* Subtle gold accent that slides in on hover */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[3px] scale-x-0 bg-gradient-to-r from-amber-400 to-amber-600 transition-transform duration-500 group-hover:scale-x-100 origin-left"
      />

      <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 text-amber-700 ring-1 ring-amber-200/60 transition-all duration-300 group-hover:from-amber-500 group-hover:to-amber-600 group-hover:text-white group-hover:ring-amber-500/40">
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </div>

      <h3 className="mb-2 font-serif text-xl font-bold leading-tight text-gray-900 md:text-2xl">
        {title}
      </h3>
      <p className="text-sm leading-relaxed text-gray-600">
        {desc}
      </p>

      {service.link && (
        <div className="mt-auto pt-5">
          <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-amber-700 transition-transform group-hover:translate-x-1">
            {t('services_v2.learn_more')}
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      )}
    </div>
  );

  if (service.link) {
    return (
      <Link
        to={service.link(lang)}
        className="block h-full"
        data-testid={`service-${service.key}`}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={`service-${service.key}`} className="h-full">
      {inner}
    </div>
  );
};

export const CoreServices = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];

  return (
    <section
      id="services"
      className="relative overflow-hidden bg-gradient-to-b from-white via-white to-amber-50/20 py-20 md:py-28"
      data-testid="core-services-section"
    >
      <div className="container mx-auto max-w-6xl px-6 md:px-8">
        {/* Header — left aligned, editorial */}
        <div className="mb-14 md:mb-20 max-w-2xl">
          <div className="mb-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-amber-700">
            <span className="h-px w-10 bg-amber-500" />
            {t('services_v2.kicker')}
          </div>
          <h2 className="font-serif text-4xl font-bold leading-[1.05] tracking-tight text-gray-900 md:text-5xl lg:text-6xl">
            {t('services_v2.title')}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-gray-600 md:text-lg">
            {t('services_v2.subtitle')}
          </p>
        </div>

        {/* Grid: 1 → 2 → 3 columns, with the 7th card spanning nicely */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:gap-6 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <ServiceCard key={s.key} service={s} t={t} lang={lang} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CoreServices;
